import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { TaskGroupBy } from "@common/types/task";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useSettings } from "@components/settings/SettingsProvider";
import { ArrowCornerIcon } from "@icons";
import { AccountingMigrationRow } from "../utils";
import styles from "./ActionsCell.module.scss";

export interface AccountingMigrationsTableCallbacks {
    removeMigration: (params: {
        workspaceId: string;
        entityId: number;
    }) => Promise<void>;
    refreshMigration: (params: {
        workspaceId: string;
        entityId: number;
    }) => Promise<void>;
    changeMigrationDates: (params: {
        workspaceId: string;
        entityId: number;
    }) => void;
}

export type AccountingMigrationsTableMeta = ViewTableMeta<
    AccountingMigrationsTableCallbacks,
    never
>;

export const ActionsCell = memo(function ActionsCell({
    row,
    table,
}: CellContext<ViewTableFeatures, AccountingMigrationRow, unknown>) {
    const navigate = useStateAwareNavigate();
    const { close: closeSettings } = useSettings();
    const meta = table.options.meta as
        | AccountingMigrationsTableMeta
        | undefined;
    const removeMigration = meta?.callbacks?.removeMigration;
    const refreshMigration = meta?.callbacks?.refreshMigration;
    const changeMigrationDates = meta?.callbacks?.changeMigrationDates;
    const workspaceId = row.original.entity.workspaceId;
    const entityId = row.original.entity.id;
    const isDuringRefresh =
        row.original.accountingMigration.refreshRequestedAt != null &&
        row.original.accountingMigration.manualJournalEntriesConvertedAt ==
            null;
    const canRefresh =
        row.original.accountingMigration.externalSystem ===
            AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE &&
        (row.original.status === "complete" || isDuringRefresh);

    const handleRemove = useCallback(async () => {
        await removeMigration?.({ workspaceId, entityId });
    }, [entityId, removeMigration, workspaceId]);

    const handleRefresh = useCallback(async () => {
        await refreshMigration?.({ workspaceId, entityId });
    }, [entityId, refreshMigration, workspaceId]);

    const handleChangeMigrationDates = useCallback(() => {
        changeMigrationDates?.({ workspaceId, entityId });
    }, [changeMigrationDates, entityId, workspaceId]);

    const handleOpenTasks = useCallback(() => {
        navigate(
            `/tasks?entityId=${entityId}&groupBy=${TaskGroupBy.TASK_GROUP}`,
        );
        closeSettings();
    }, [closeSettings, entityId, navigate]);

    const showRemove = Boolean(removeMigration);
    const showRefresh = Boolean(refreshMigration) && canRefresh;
    const showChangeMigrationDates = Boolean(changeMigrationDates);

    return (
        <ViewTableCell captureClicks className={styles.actionsCell}>
            <Button
                variant="secondary"
                size="sm"
                iconOnly
                title="View migration tasks"
                aria-label="View migration tasks"
                className={viewTableStyles.showOnHover}
                onClick={handleOpenTasks}
            >
                <ArrowCornerIcon />
            </Button>
            {(showRemove || showRefresh || showChangeMigrationDates) && (
                <Menu.More className={viewTableStyles.showOnHover}>
                    {showChangeMigrationDates && (
                        <Menu.Item onClick={handleChangeMigrationDates}>
                            Change migration dates
                        </Menu.Item>
                    )}
                    {showRefresh && (
                        <Menu.Item onClick={handleRefresh}>
                            Refresh migration
                        </Menu.Item>
                    )}
                    {showRemove && (
                        <Menu.Item onClick={handleRemove} destructive>
                            Remove migration
                        </Menu.Item>
                    )}
                </Menu.More>
            )}
        </ViewTableCell>
    );
});
